import { AnimatePresence, m } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

interface CopyUrlButtonProps {
  children: React.ReactNode;
  className?: string;
}

function CopyUrlButton({ children, className }: CopyUrlButtonProps) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copyCurrentUrl = async () => {
    const url = window.location.href;

    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const textarea = document.createElement('textarea');

      textarea.value = url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';

      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    setCopied(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <button type="button" className={className} onClick={copyCurrentUrl}>
      <span className="underline opacity-60 hover:opacity-100">{children}</span>

      <AnimatePresence>
        {copied && (
          <m.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="inline-flex items-center gap-2 ml-3 text-sm copy-url__feedback"
            aria-live="polite"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="shrink-0 size-3"
            >
              <path d="m10 15.17 9.192-9.191 1.414 1.414L10 17.999l-6.364-6.364 1.414-1.414z" />
            </svg>
            Kopierad
          </m.span>
        )}
      </AnimatePresence>
    </button>
  );
}

export default CopyUrlButton;
